import React, { useContext, useLayoutEffect, useState, useEffect } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMeasure, useWindowSize } from 'react-use';
import ThemeContext from '../../contexts/themeContext';
import Button from '../../components/bootstrap/Button';
import Portal from '../Portal/Portal';
import useDarkMode from '../../hooks/useDarkMode';
import { createPortal } from 'react-dom';
import { usePoints } from '../../contexts/PointsContext.js';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../../contexts/CartContext';
import Dropdown, {
	DropdownItem,
	DropdownMenu,
	DropdownToggle,
} from '../../components/bootstrap/Dropdown';
import { userAuth } from '../../contexts/userContext';
import Icon from '../../components/icon/Icon';
import apiService from '../../api/apiService';
import { useTranslation } from 'react-i18next';
import { useLanguageSwitch } from '../../contexts/LanguageSwitchContext';
import CommonTimer from '../../pages/common/vhelper/commonTimer';
import OffCanvas, {
	OffCanvasBody,
	OffCanvasHeader,
	OffCanvasTitle,
} from '../../components/bootstrap/OffCanvas';
import Alert from '../../components/bootstrap/Alert';

export const HeaderLeft = ({ children, className }) => {
	return <div className={classNames('header-left', 'col-md', className)}>{children}</div>;
};
HeaderLeft.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
HeaderLeft.defaultProps = {
	className: null,
};

export const HeaderRight = ({ children, className }) => {
	const [ref, { height }] = useMeasure();

	const root = document.documentElement;
	root.style.setProperty('--header-right-height', `${height}px`);

	return (
		<div ref={ref} className={classNames('header-right', 'col-md-auto', className)}>
			{children}
		</div>
	);
};
HeaderRight.propTypes = {
	children: PropTypes.node.isRequired,
	className: PropTypes.string,
};
HeaderRight.defaultProps = {
	className: null,
};

const Header = ({ children, hasLeftMobileMenu, hasRightMobileMenu }) => {
	const { themeStatus } = useDarkMode();
	const { t } = useTranslation();
	const {
		itemsCount,
		clearCountdown,
		notiCount,
		notiList,
		setNotiList,
		setNotiCount,
		setItemsCount,
	} = useContext(CartContext);
	const { darkModeStatus, setDarkModeStatus } = useDarkMode();
	const styledBtn = {
		color: darkModeStatus ? 'dark' : 'light',
		hoverShadow: 'default',
		isLight: !darkModeStatus,
		size: 'lg',
	};
	const [windowWidth, setWindowWidth] = useState(window.innerWidth);
	const { setToken, token } = useContext(userAuth);
	const MOBILE_BREAKPOINT = Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE) || 576;
	const isMobile = windowWidth < MOBILE_BREAKPOINT;
	const [mobileContainer, setMobileContainer] = useState(null);
	const { availablePoints, pointsError, loadingPoints, fetchAvailablePoints } = usePoints();
	const navigate = useNavigate();
	const windowsWidth = useWindowSize().width;
	const [refMobileHeader, sizeMobileHeader] = useMeasure();
	const [refHeader, sizeHeader] = useMeasure();
	const [offcanvasStatus, setOffcanvasStatus] = useState(false);
	const [pendingParams, setPendingParams] = useState({
		appid: '',
	});

	const root = document.documentElement;
	root.style.setProperty('--mobile-header-height', `${sizeMobileHeader.height}px`);
	root.style.setProperty('--header-height', `${sizeHeader.height}px`);

	const {
		asideStatus,
		setAsideStatus,
		leftMenuStatus,
		setLeftMenuStatus,
		rightMenuStatus,
		setRightMenuStatus,
	} = useContext(ThemeContext);

	useLayoutEffect(() => {
		if (
			(asideStatus || leftMenuStatus || rightMenuStatus) &&
			windowsWidth < process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE
		)
			document.body.classList.add('overflow-hidden');
		return () => {
			document.body.classList.remove('overflow-hidden');
		};
	});

	// Create a portal target in the mobile header for compact right-side actions
	useEffect(() => {
		const MOBILE_BREAKPOINT = Number(process.env.REACT_APP_MOBILE_BREAKPOINT_SIZE) || 576;
		const row = document.querySelector('.mobile-header .container-fluid .row');
		if (!row) return undefined;

		let container = document.getElementById('mobile-header-right-actions');
		if (!container) {
			container = document.createElement('div');
			container.id = 'mobile-header-right-actions';
			container.className = 'col-auto d-flex align-items-center';
		}

		if (windowWidth < MOBILE_BREAKPOINT) {
			// append if not present
			if (!row.contains(container)) row.appendChild(container);
			setMobileContainer(container);
		} else {
			// remove if present
			if (row.contains(container)) row.removeChild(container);
			setMobileContainer(null);
		}

		return () => {
			if (row.contains(container)) row.removeChild(container);
			setMobileContainer(null);
		};
	}, [windowWidth]);

	const logouthandle = async () => {
		const item = { action: 'logout' };
		try {
			const flechdata = await apiService(item, token, setToken, navigate);
			if (flechdata.type === 'success') {
				setToken({});
				localStorage.removeItem('user-auth');
				setItemsCount(0);
				clearCountdown();
				navigate('/');
			} else {
				console.error('Error fetching services:', flechdata.message);
			}
		} catch (error) {
			console.error('Error fetching services:', error);
		}
	};
	const OnClickToCustomer = () => {
		navigate('/cart');
	};

	const fetchNotification = async () => {
		const item = { action: 'get_user_notification_list' };

		const flechdata = await apiService(item, token, setToken, navigate);
		if (flechdata.type === 'success') {
			setNotiList(flechdata.data);
			setNotiCount(flechdata.TotalRecords);
		}
	};

	const delNotification = async (id) => {
		const formData = {};

		formData['notification_id'] = id;

		formData['action'] = 'update_user_notification_status';
		try {
			const flechdata = await apiService(formData, token, setToken, navigate);

			if (flechdata.type === 'Success' || flechdata.type === 'success') {
				setNotiCount([]);
			} else {
			}
		} catch (error) {
			console.error('Error fetching data:', error);
		}
	};

	const mobilePortal = mobileContainer
		? createPortal(
				<>
					<div className='col-auto'>
						<div className='col-auto position-relative'>
							<div style={{ marginTop: '10px' }}>
								<CommonTimer />
							</div>
						</div>
					</div>

					<div className='col-auto' style={{ marginLeft: 10 }}>
						<div className='col-auto position-relative'>
							<Button {...styledBtn} icon='Cart' onClick={OnClickToCustomer} />

							{(itemsCount || itemsCount !== 0) && (
								<span className='position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger'>
									{itemsCount < 99 ? itemsCount : '99+'}
								</span>
							)}
						</div>
					</div>

					<div className='col-auto' style={{ marginLeft: 10 }}>
						<div className='col-auto position-relative'>
							<Button
								{...styledBtn}
								icon='Notifications'
								onClick={() => {
									setOffcanvasStatus(true);
									fetchNotification();
								}}
								aria-label='Notifications'
							/>
							{notiCount > 0 && (
								<span className='position-absolute top-0 start-90 translate-middle badge rounded-pill bg-danger'>
									{notiCount < 99 ? notiCount : '99+'}
								</span>
							)}
						</div>
					</div>

					<OffCanvas
						id='notificationCanvas'
						titleId='offcanvasExampleLabel'
						placement='end'
						isOpen={offcanvasStatus}
						setOpen={setOffcanvasStatus}>
						<OffCanvasHeader
							setOpen={setOffcanvasStatus}
							style={{ paddingTop: '30px' }}>
							<OffCanvasTitle
								id='offcanvasExampleLabel'
								style={{
									fontSize: '20px',
									padding: 0,
									margin: 0,
									fontWeight: 500,
								}}>
								{t('Notifications')}
							</OffCanvasTitle>
							{notiList.length > 0 && (
								<Button
									color='danger'
									icon='Delete'
									style={{ marginLeft: '100px' }}
									onClick={() => delAllNotification()}>
									Clear All
								</Button>
							)}
						</OffCanvasHeader>
						<OffCanvasBody style={{ paddingTop: '0px' }}>
							<div className='notifications-body'>
								{notiList && notiList.length > 0 ? (
									notiList.map((item) => {
										let pathname = '';
										switch (item.notify_type) {
											case 'add_appointment':
												pathname = '/appointment/pending';
												break;
											case 'cancel_order':
											case 'complete_order':
											case 'add_order':
												pathname = '/orders';
												break;
											case 'complete_refund':
											case 'cancel_refund':
												pathname = '/refunds/completed';
												break;
											case 'cancel_appointment':
												pathname = '/appointment/cancelled';
												break;
											case 'complete_appointment':
												pathname = '/appointment/completed';
												break;
											case 'reminder':
												pathname = '/appointment/pending';
												break;
											default:
												pathname = '/';
										}

										return (
											<Alert
												icon='NotificationsNone'
												isLight
												color='info'
												className='flex-nowrap'
												isDismissible
												onClose={() => delNotification(item.id)}>
												<div
													style={{
														display: 'flex',
														flexDirection: 'column',
														cursor: 'pointer',
													}}
													onClick={() => {
														if (
															item.notify_type ===
																'add_appointment' ||
															item.notify_type ===
																'cancel_appointment' ||
															item.notify_type ===
																'complete_appointment' ||
															item.notify_type === 'reminder'
														) {
															navigate(
																`${pathname}?appid=${item.booking_code}&notify_id=${item.id}`,
															);
														} else if (
															item.notify_type === 'cancel_order' ||
															item.notify_type === 'complete_order' ||
															item.notify_type === 'add_order'
														) {
															navigate(
																`${pathname}?appid=${item.order_code}&notify_id=${item.id}`,
															);
														} else if (
															item.notify_type ===
																'complete_refund' ||
															item.notify_type === 'cancel_refund'
														) {
															navigate(
																`${pathname}?appid=${item.order_code}&notify_id=${item.id}`,
															);
														}
														setOffcanvasStatus(false);
														setPendingParams(item.appointment_code);
													}}>
													<strong>{item.notify_title}</strong>
													<p>{item.notify_full_desc}</p>
													<small
														style={{
															textAlign: 'left',
														}}>
														{item.created_at}
													</small>
												</div>
											</Alert>
										);
									})
								) : (
									<p style={{ marginTop: '120px', marginLeft: '135px' }}>
										No notifications
									</p>
								)}
							</div>
						</OffCanvasBody>
					</OffCanvas>

					<div className='col-auto' style={{ marginLeft: 10 }}>
						<Dropdown>
							<DropdownToggle hasIcon={false}>
								<Button {...styledBtn} icon='Person' aria-label='Quick menu' />
							</DropdownToggle>
							<DropdownMenu
								isAlignmentEnd
								size={windowWidth < 740 ? 'md' : 'lg'}
								className='py-0 overflow-hidden'>
								<div className='row g-0'>
									<div
										className={classNames(
											'col-12',
											'p-4',
											'd-flex justify-content-center',
											'fw-medium fs-5',
											'text-info',
											'border-bottom border-info',
											'bg-lo25-info',
										)}>
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												alignItems: 'center',
											}}>
											<span style={{ color: 'white', fontSize: '16px' }}>
												{token.name}
											</span>
											<span style={{ color: 'white', fontSize: '12px' }}>
												{token.email}
											</span>
										</div>
									</div>
									<div
										className={classNames(
											'col-12 p-4 transition-base cursor-pointer',

											{ 'border-dark': darkModeStatus },
										)}>
										<div
											className='d-flex flex-column align-items-center justify-content-center'
											onClick={logouthandle}>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '10px',
													color: '#e17f7f',
												}}>
												<Icon icon='Logout' size='3x' />
												<span
													style={{ fontWeight: '500', fontSize: '14px' }}>
													{t('Log Out')}
												</span>
											</div>
										</div>
									</div>
								</div>
							</DropdownMenu>
						</Dropdown>
					</div>

					<div className='col-auto' style={{ marginLeft: 10 }}>
						<Button
							aria-label='Toggle Right Menu'
							className='mobile-header-toggle'
							size='lg'
							color={rightMenuStatus ? 'primary' : themeStatus}
							isLight={rightMenuStatus}
							icon={rightMenuStatus ? 'Menu' : 'MenuOpen'}
							onClick={() => {
								setAsideStatus(false);
								setLeftMenuStatus(false);
								setRightMenuStatus(!rightMenuStatus);
							}}
						/>
					</div>
				</>,
				mobileContainer,
		  )
		: null;

	return (
		<>
			<header ref={refMobileHeader} className='mobile-header'>
				<div className='container-fluid'>
					<div className='row'>
						<div className='col'>
							<Button
								aria-label='Toggle Aside'
								className='mobile-header-toggle'
								size='lg'
								color={asideStatus ? 'primary' : themeStatus}
								isLight={asideStatus}
								icon={asideStatus ? 'FirstPage' : 'LastPage'}
								onClick={() => {
									setAsideStatus(!asideStatus);
									setLeftMenuStatus(false);
									setRightMenuStatus(false);
								}}
							/>
						</div>

						{hasRightMobileMenu && <>{mobilePortal}</>}
					</div>
				</div>
			</header>
			<header
				ref={refHeader}
				className={classNames('header', {
					'header-left-open': leftMenuStatus,
					'header-right-open': rightMenuStatus,
				})}>
				<div className='container-fluid'>
					<div className='row d-flex align-items-center'>
						{children}
						{(leftMenuStatus || rightMenuStatus) && (
							<Portal>
								<div
									role='presentation'
									className={classNames('header-overlay', {
										'header-overlay-left-menu': leftMenuStatus,
										'header-overlay-right-menu': rightMenuStatus,
									})}
									onClick={() => {
										setAsideStatus(false);
										setLeftMenuStatus(false);
										setRightMenuStatus(false);
									}}
								/>
							</Portal>
						)}
					</div>
				</div>
			</header>
		</>
	);
};
Header.propTypes = {
	children: PropTypes.node.isRequired,
	hasLeftMobileMenu: PropTypes.bool,
	hasRightMobileMenu: PropTypes.bool,
};
Header.defaultProps = {
	hasLeftMobileMenu: true,
	hasRightMobileMenu: true,
};

export default Header;
